import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  yoboulmakoApi,
  type CreateLogisticsClientInput,
  type UpdateLogisticsClientInput,
  type PerClientMetric,
  type CodCollectionsParams,
  type UpdateCodStatusInput,
} from "@/lib/admin-api/yoboulmako";

// Re-export so pages can import the type from this hook module
export type { PerClientMetric };

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  clients: ["yoboulmako-clients"] as const,
  client: (id: string) => ["yoboulmako-client", id] as const,
  dashboard: (clientId?: string) => ["yoboulmako-dashboard", clientId ?? "all"] as const,
  cod: (params: CodCollectionsParams) => ["yoboulmako-cod", params] as const,
  codSummary: (clientId: string, dateFrom?: string, dateTo?: string) =>
    ["yoboulmako-cod-summary", clientId, dateFrom, dateTo] as const,
} as const;

// ---------------------------------------------------------------------------
// Dashboard KPIs
// ---------------------------------------------------------------------------

export function useYoboulmakoDashboard(clientId?: string) {
  return useQuery({
    queryKey: KEYS.dashboard(clientId),
    queryFn: () => yoboulmakoApi.dashboard.get(clientId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Clients — list
// ---------------------------------------------------------------------------

export function useLogisticsClients() {
  return useQuery({
    queryKey: KEYS.clients,
    queryFn: () => yoboulmakoApi.clients.list(),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Clients — single
// ---------------------------------------------------------------------------

export function useLogisticsClient(id: string) {
  return useQuery({
    queryKey: KEYS.client(id),
    queryFn: () => yoboulmakoApi.clients.get(id),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------------------
// Clients — create
// ---------------------------------------------------------------------------

export function useCreateLogisticsClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLogisticsClientInput) =>
      yoboulmakoApi.clients.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.clients });
    },
  });
}

// ---------------------------------------------------------------------------
// Clients — update
// ---------------------------------------------------------------------------

export function useUpdateLogisticsClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { readonly id: string; readonly data: UpdateLogisticsClientInput }) =>
      yoboulmakoApi.clients.update(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: KEYS.clients });
      queryClient.invalidateQueries({ queryKey: KEYS.client(variables.id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Clients — deactivate (soft delete)
// ---------------------------------------------------------------------------

export function useDeactivateLogisticsClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => yoboulmakoApi.clients.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.clients });
    },
  });
}

// ---------------------------------------------------------------------------
// API key generation
// ---------------------------------------------------------------------------

export function useGenerateApiKey() {
  return useMutation({
    mutationFn: (clientId: string) => yoboulmakoApi.clients.generateApiKey(clientId),
    // Do NOT invalidate or cache — raw key must only be shown once
  });
}

// ---------------------------------------------------------------------------
// COD collections
// ---------------------------------------------------------------------------

export function useCodCollections(params: CodCollectionsParams) {
  return useQuery({
    queryKey: KEYS.cod(params),
    queryFn: () => yoboulmakoApi.cod.list(params),
    staleTime: 30_000,
    enabled: Boolean(params.client_id),
  });
}

export function useCodSummary(
  clientId: string,
  params?: { date_from?: string; date_to?: string },
) {
  return useQuery({
    queryKey: KEYS.codSummary(clientId, params?.date_from, params?.date_to),
    queryFn: () => yoboulmakoApi.cod.summary(clientId, params),
    staleTime: 30_000,
    enabled: Boolean(clientId),
  });
}

export function useUpdateCodStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { readonly id: string; readonly data: UpdateCodStatusInput }) =>
      yoboulmakoApi.cod.updateStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["yoboulmako-cod"] });
      queryClient.invalidateQueries({ queryKey: ["yoboulmako-cod-summary"] });
    },
  });
}
